import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import butterflyApi from '../ButterflyApi';
import MobilePage from '../UiComponents/MobilePage';
import { isLogin } from '../Utils/Cookies';
import { callName } from '../Utils/Strings';
import { getAppProductionStatusText, hasPaidAppProduction, replaceAppTitleCallName } from './appProductionStatus';
import './AppPayment.css';

const NICEPAY_SDK_URL = 'https://pay.nicepay.co.kr/v1/js/';
const APP_PAYMENT_ORDER_ID_KEY = 'appPaymentOrderId';
const APP_PAYMENT_HERO_NO_KEY = 'appPaymentHeroNo';

const formatPrice = (price) => {
  if (price === undefined || price === null) return ''
  return `${new Intl.NumberFormat('ko-KR').format(price)}원`
}

const getPurchasePrice = (app) => {
  if (!app || app.salePrice === undefined || app.salePrice === null) return
  return Math.max(app.salePrice - (app.discountPrice || 0), 0)
}

const getAppName = (app, heroName) => {
  return replaceAppTitleCallName(app?.appName, app, heroName)
}

const getHeroName = (hero) => {
  if (!hero) return ''
  return `${hero.lastName || ''}${hero.firstName || ''}` || callName(hero.firstName)
}

const getLocalHeroes = () => {
  try {
    return JSON.parse(localStorage.getItem('heroes')) || []
  } catch (e) {
    return []
  }
}

const loadNicepaySdk = () => {
  if (window.AUTHNICE && window.AUTHNICE.requestPay) {
    return Promise.resolve()
  }

  return new Promise((resolve, reject) => {
    const existingScript = document.querySelector(`script[src="${NICEPAY_SDK_URL}"]`)

    if (existingScript) {
      existingScript.addEventListener('load', resolve, { once: true })
      existingScript.addEventListener('error', reject, { once: true })
      return
    }

    const script = document.createElement('script')
    script.src = NICEPAY_SDK_URL
    script.async = true
    script.onload = resolve
    script.onerror = reject
    document.body.appendChild(script)
  })
}

const getNicepayValue = (result, keys) => {
  return keys.map(key => result && result[key]).find(value => value !== undefined && value !== null && value !== '')
}

const AppPayment = () => {
  const navigate = useNavigate();
  const { appNo } = useParams();
  const heroes = useMemo(() => getLocalHeroes(), [])
  const [app, setApp] = useState()
  const [selectedHeroNo, setSelectedHeroNo] = useState(isLogin() ? localStorage.getItem('heroNo') : '')
  const [loading, setLoading] = useState(true)
  const [submitting, setSubmitting] = useState(false)
  const [readyPayment, setReadyPayment] = useState()
  const [errorMessage, setErrorMessage] = useState('')
  const [paymentMessage, setPaymentMessage] = useState('')
  const [duplicateProduction, setDuplicateProduction] = useState(false)

  const selectedHero = heroes.find(hero => `${hero.heroNo}` === `${selectedHeroNo}`)
  const heroCallName = selectedHero ? callName(selectedHero.firstName) : '하늘이'
  const displayAppName = app ? getAppName(app, heroCallName) : ''
  const productionStatusText = getAppProductionStatusText(app?.productionStatus)
  const paymentPagePath = `/apps/${appNo}/payment`
  const canRunApp = Boolean(app?.resultUrl)
  const paymentLocked = hasPaidAppProduction(app) && !canRunApp
  const loggedIn = Boolean(isLogin())
  const isFairytale = app?.category === 'fairytale'
  const productType = { fairytale: '동화', game: '게임', video: '영상', life: '앱' }[app?.category] || '앱'
  const catalogPath = app?.category ? `/apps?category=${encodeURIComponent(app.category)}` : '/apps'
  const productIcon = app?.category === 'game' ? '/image/main/games.png' : '/image/main/books.png'
  const purchasePrice = getPurchasePrice(app)
  const displayDescription = (app?.description || '').replace(/\{callName\}/g, heroCallName)

  const loadApp = useCallback(async () => {
    setLoading(true)
    const apps = await butterflyApi.getApps('', isLogin() ? selectedHeroNo : undefined)
    setApp((apps || []).find(app => `${app.appNo}` === `${appNo}`))
    setLoading(false)
  }, [appNo, selectedHeroNo])

  const handlePaymentApiError = (error) => {
    const status = error.response && error.response.status
    const message = (error.response && error.response.data && error.response.data.message) || error.message

    if (status === 409) {
      setDuplicateProduction(true)
      setErrorMessage('이미 진행 중인 제작 신청이 있습니다.')
      return
    }

    if (status === 400 && message === 'Hero가 없습니다.') {
      setErrorMessage('선택한 주인공 정보를 확인할 수 없습니다. 주인공을 다시 선택해 주세요.')
      return
    }

    setErrorMessage(message || '결제를 준비하는 중 문제가 발생했습니다.')
  }

  const handleApproveApiError = (error) => {
    const message = (error.response && error.response.data && error.response.data.message) || error.message
    setErrorMessage(message || '결제 승인에 실패했습니다. 결제 준비부터 다시 시도해 주세요.')
  }

  const approvePayment = async (ready, nicepayResult) => {
    const authResultCode = getNicepayValue(nicepayResult, ['authResultCode', 'resultCode'])

    if (authResultCode && authResultCode !== '0000') {
      const message = getNicepayValue(nicepayResult, ['authResultMsg', 'resultMsg', 'errorMsg'])
      setErrorMessage(message || '결제가 완료되지 않았습니다.')
      setSubmitting(false)
      setPaymentMessage('')
      return
    }

    const tid = getNicepayValue(nicepayResult, ['tid', 'TID'])
    const authToken = getNicepayValue(nicepayResult, ['authToken', 'AuthToken'])

    if (!tid || !authToken) {
      setErrorMessage('결제 인증 결과를 확인할 수 없습니다. 결제창 연동값을 확인해 주세요.')
      setSubmitting(false)
      setPaymentMessage('')
      return
    }

    setPaymentMessage('결제 승인을 진행하고 있습니다.')
    const approve = await butterflyApi.approveAppPayment(
      ready.orderId,
      ready.amount,
      tid,
      authToken,
      handleApproveApiError,
    )

    setSubmitting(false)
    setPaymentMessage('')

    if (!approve) return

    navigate(`/orders/${approve.productionNo}`)
  }

  const openNicepayPayment = async (ready) => {
    await loadNicepaySdk()

    if (!window.AUTHNICE || !window.AUTHNICE.requestPay) {
      throw new Error('Nicepay 결제창을 불러오지 못했습니다.')
    }

    window.AUTHNICE.requestPay({
      clientId: ready.clientKey,
      method: 'card',
      orderId: ready.orderId,
      amount: ready.amount,
      goodsName: ready.appName,
      returnUrl: `http://localhost:18009/api/apps/payments/approve`,
      fnSuccess: result => approvePayment(ready, result),
      fnComplete: result => approvePayment(ready, result),
      fnClose: () => {
        setSubmitting(false)
        setPaymentMessage('')
      },
      fnError: result => {
        setSubmitting(false)
        setPaymentMessage('')
        setErrorMessage((result && result.errorMsg) || '결제창에서 오류가 발생했습니다.')
      },
    })
  }

  const startPayment = async () => {
    if (!isLogin()) {
      navigate(`/login?redirect=${encodeURIComponent(paymentPagePath)}`)
      return
    }

    if (!selectedHeroNo) {
      alert('제작할 주인공을 선택해 주세요.')
      return
    }

    if (hasPaidAppProduction(app)) {
      setDuplicateProduction(true)
      setErrorMessage('이미 결제가 완료된 항목입니다. 주문내역에서 제작 상태를 확인해 주세요.')
      return
    }

    setSubmitting(true)
    setDuplicateProduction(false)
    setErrorMessage('')
    setReadyPayment()
    setPaymentMessage('결제 금액을 확인하고 있습니다.')

    const ready = await butterflyApi.readyAppPayment(appNo, Number(selectedHeroNo), handlePaymentApiError)

    if (!ready) {
      setSubmitting(false)
      setPaymentMessage('')
      return
    }

    try {
      sessionStorage.setItem(APP_PAYMENT_ORDER_ID_KEY, ready.orderId)
      sessionStorage.setItem(APP_PAYMENT_HERO_NO_KEY, selectedHeroNo)
    } catch (e) {
      // 세션 저장이 막혀도 결제 진행에는 영향이 없다.
    }

    setReadyPayment(ready)
    setPaymentMessage('결제창을 열고 있습니다.')

    try {
      await openNicepayPayment(ready)
    } catch (error) {
      setSubmitting(false)
      setPaymentMessage('')
      setErrorMessage(error.message || '결제창을 여는 중 문제가 발생했습니다.')
    }
  }

  const onHeroSelect = (heroNo) => {
    localStorage.setItem('heroNo', heroNo)
    setSelectedHeroNo(heroNo)
    setDuplicateProduction(false)
    setErrorMessage('')
    setPaymentMessage('')
    setReadyPayment()
  }

  useEffect(() => {
    window.scrollTo({ top: 0, left: 0 })
    loadApp()
  }, [appNo, loadApp])

  if (loading) {
    return <MobilePage className='storybook-page payment-page' loading loadingMessage='신청 정보를 불러오고 있습니다.' />
  }

  if (!app) {
    return <MobilePage className='storybook-page payment-page'>
      <main className='payment-content'>
        <Link className='book-text-link payment-back' to='/apps'><span aria-hidden='true'>‹</span> 신청 목록으로</Link>
        <section className='payment-empty payment-panel'>
          <img src='/image/main/books.png' alt='' width='56' height='56' />
          <h1 className='book-title'>신청 정보를 찾을 수 없어요</h1>
          <p>신청 목록에서 원하는 항목을 다시 골라주세요.</p>
          <Link className='book-primary-button' to='/apps'>신청 목록으로 돌아가기 <span aria-hidden='true'>→</span></Link>
        </section>
      </main>
    </MobilePage>
  }

  return <MobilePage className='storybook-page payment-page'>
    <main className={`payment-content${loggedIn && heroes.length > 0 ? ' payment-content--with-action' : ''}`}>
      <Link className='book-text-link payment-back' to={catalogPath}><span aria-hidden='true'>‹</span> {productType} 목록으로</Link>

      <header className='payment-intro'>
        <p className='book-eyebrow'><span aria-hidden='true'>✦</span> 우리 아이를 위한 특별한 선물</p>
        <div className='payment-title-row'>
          <h1 className='book-title'>{productType} 제작 신청</h1>
          <img src={productIcon} alt='' width='56' height='56' />
        </div>
        <p className='book-description'>
          {isFairytale ? <>우리 아이가 주인공이 되는 이야기.<br />특별한 동화 한 편을 선물해 주세요.</> : <>우리 아이를 위한 {productType}.<br />함께할 주인공을 선택해 주세요.</>}
        </p>
      </header>

      <section className='payment-section' aria-labelledby='payment-product-heading'>
        <h2 className='payment-section-heading' id='payment-product-heading'><span aria-hidden='true'>01</span> 선택한 {productType}</h2>
        <article className='payment-panel payment-product'>
          <div className={`payment-product-image${app.imageUrl ? '' : ' payment-product-image--fallback'}`}>
            <img src={app.imageUrl || productIcon} alt='' width='88' height='100' />
          </div>
          <div className='payment-product-copy'>
            <span className='payment-product-label'>우리 아이 맞춤 {productType}</span>
            <h3>{displayAppName}</h3>
            {displayDescription && <p>{displayDescription}</p>}
          </div>
          {productionStatusText && <div className='payment-production-status' data-status={app.productionStatus}>
            <span>제작 상태</span><strong>{productionStatusText}</strong>
          </div>}
        </article>
      </section>

      <section className='payment-section' aria-labelledby='payment-hero-heading'>
        <h2 className='payment-section-heading' id='payment-hero-heading'><span aria-hidden='true'>02</span> {isFairytale ? '이야기의 주인공' : '함께할 주인공'}</h2>

        {!loggedIn && <div className='payment-panel payment-empty'>
          <span className='payment-empty-symbol' aria-hidden='true'>✧</span>
          <h3>우리 아이의 이야기를 시작해 볼까요?</h3>
          <p>로그인하면 주인공을 선택하고<br />{productType} 제작을 신청할 수 있어요.</p>
          <Link className='book-primary-button' to={`/login?redirect=${encodeURIComponent(paymentPagePath)}`}>
            로그인하고 시작하기 <span aria-hidden='true'>→</span>
          </Link>
        </div>}

        {loggedIn && heroes.length === 0 && <div className='payment-panel payment-empty'>
          <span className='payment-empty-symbol' aria-hidden='true'>✧</span>
          <h3>먼저 주인공을 소개해 주세요</h3>
          <p>아이의 이름과 사진을 등록하면<br />맞춤 {productType} 제작을 신청할 수 있어요.</p>
          <Link className='book-primary-button' to='/heroes/add'>주인공 추가하기 <span aria-hidden='true'>＋</span></Link>
        </div>}

        {loggedIn && heroes.length > 0 && <>
          <p className='payment-section-description' id='payment-hero-description'>누구를 위한 선물인가요? 주인공을 선택해 주세요.</p>
          <fieldset className='payment-heroes' disabled={submitting} aria-describedby='payment-hero-description'>
            <legend className='sr-only'>제작할 주인공 선택</legend>
            {heroes.map(hero => {
              const selected = `${hero.heroNo}` === `${selectedHeroNo}`

              return <label key={hero.heroNo} className={`payment-hero${selected ? ' payment-hero--selected' : ''}`}>
                <input
                  type='radio'
                  name='payment-hero'
                  value={hero.heroNo}
                  checked={selected}
                  onChange={() => onHeroSelect(hero.heroNo)}
                  aria-label={getHeroName(hero)}
                />
                <img src={hero.graphicImageUrl || hero.imageUrl || '/image/no_profile_image.png'} alt='' width='52' height='52' />
                <span className='payment-hero-copy'>
                  <strong>{getHeroName(hero)}</strong>
                  <span>{callName(hero.firstName)} 이름으로 제작해요</span>
                </span>
                {selected && <span className='payment-hero-selected' aria-hidden='true'>선택</span>}
              </label>
            })}
          </fieldset>
        </>}
      </section>

      <section className='payment-section' aria-labelledby='payment-price-heading'>
        <h2 className='payment-section-heading' id='payment-price-heading'><span aria-hidden='true'>03</span> 결제 금액</h2>
        <div className='payment-panel payment-summary'>
          <dl className='payment-price'>
            <div>
              <dt>판매가</dt>
              <dd>{app.discountPrice > 0 ? <del>{formatPrice(app.salePrice)}</del> : formatPrice(app.salePrice)}</dd>
            </div>
            {app.discountPrice > 0 && <div className='payment-price-discount'>
              <dt>할인 금액</dt>
              <dd>−{formatPrice(app.discountPrice)}</dd>
            </div>}
            <div className='payment-price-total'>
              <dt>최종 결제 금액</dt>
              <dd><strong>{formatPrice(purchasePrice)}</strong></dd>
            </div>
          </dl>
          {loggedIn && selectedHero && <p className='payment-summary-hero'>
            <span aria-hidden='true'>✓</span> <strong>{getHeroName(selectedHero)}</strong> 주인공으로 제작해요
          </p>}
        </div>
      </section>

      <aside className='payment-guide' aria-labelledby='payment-guide-heading'>
        <span className='payment-guide-icon' aria-hidden='true'>✧</span>
        <div>
          <h2 id='payment-guide-heading'>소중한 선물을 정성껏 준비할게요</h2>
          <p>{isFairytale && <>동화는 신청 순서대로 제작돼요.<br /></>}제작 진행 상황은 주문내역에서 확인할 수 있어요.</p>
          <Link className='book-text-link' to='/orders'>주문내역 보기 <span aria-hidden='true'>↗</span></Link>
        </div>
      </aside>

      {readyPayment && <div className='payment-notice'>
        <strong>결제 정보 확인</strong>
        <p>{readyPayment.appName} / {formatPrice(readyPayment.amount)}</p>
      </div>}

      <div role='status' aria-live='polite' aria-atomic='true'>
        {paymentMessage && <p className='payment-notice payment-progress'>{paymentMessage}</p>}
      </div>

      {errorMessage && <div className='book-error payment-error' role='alert'>
        <p>{errorMessage}</p>
        {duplicateProduction && <Link className='book-text-link' to='/orders'>주문내역 보기 <span aria-hidden='true'>→</span></Link>}
      </div>}
    </main>

    {loggedIn && heroes.length > 0 && <div className='payment-action'>
        <p>{canRunApp ? '우리 아이를 위한 선물이 완성됐어요.' : paymentLocked ? '이미 신청한 선물의 제작 상태를 확인해 주세요.' : selectedHero ? `${getHeroName(selectedHero)} 주인공으로 만드는 특별한 ${productType}` : '함께할 주인공을 선택해 주세요.'}</p>
        <button
          type='button'
          className='book-primary-button payment-submit'
          disabled={!canRunApp && !paymentLocked && (submitting || !selectedHeroNo)}
          aria-busy={submitting}
          onClick={() => {
            if (canRunApp) {
              window.location.href = app.resultUrl
              return
            }

            if (paymentLocked) {
              navigate('/orders')
              return
            }

            startPayment()
          }}
        >
          <span>{canRunApp ? (isFairytale ? '완성된 동화 보기' : '실행하기') : paymentLocked ? '주문내역 보기' : submitting ? '결제 진행 중...' : `${formatPrice(purchasePrice)} 결제하기`}</span>
          <span className='payment-submit-arrow' aria-hidden='true'>{submitting ? '…' : '→'}</span>
        </button>
      </div>}
  </MobilePage>
}

export default AppPayment
